import { Component, computed, ElementRef, HostListener, Inject, PLATFORM_ID, signal, ViewChild, ViewEncapsulation, Renderer2 } from '@angular/core';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { AccountService, AuthService, ProductCollectionService, QuoteOrdersService, SampleOrderService } from '@coreApiServices';
import { CommonConstants, ROUTES, STORAGE } from '@coreConstant';
import { HttpErrorResponse } from '@angular/common/http';
import { PaginationModel, RequestParamModel, UserModel } from '@coreModels';
import { UtilityService, SubscribersService, TokenStorageService, ToastService } from '@coreServices';
import { CurrencyPipe, isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { HandleImageErrorDirective } from '../../../directives/handle-image-error.directive';
import { askLogoutConfirmation } from '../../../utils/logout-confirmation';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [NgbCollapseModule, RouterModule, CurrencyPipe, HandleImageErrorDirective, NgbTooltipModule, NgTemplateOutlet],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None, // Use this to apply global styles
})
export class HeaderComponent {
  // State variables for collapsed navbar and sticky header
  isHidden = signal(false);
  isSticky = signal(false);
  isSearchActive = signal(false);
  lastScrollTop = 0;
  // Variables for handling user authentication
  public isUserAuthenticated: boolean = false;

  // Collections data for flooring and walling
  flooringCollectionList!: any;
  wallingCollectionList!: any;
  ROUTES = ROUTES;
  @ViewChild('menuIcon') menuIcon!: ElementRef<HTMLButtonElement>;
  isCreditEarnData: any;

  // Constant references
  s3BucketImageURL = CommonConstants.IMAGE_KIT_ENDPOINT;
  wallingTypeName = CommonConstants.WALLING_TYPE_NAME;
  flooringTypeName = CommonConstants.FLOORING_TYPE_NAME;

  /**
   * TEMPORARY. Hides the GatherCo Credit pill in the header while the Ask Pebbl entry point
   * stands in its slot. Nothing else was removed - the pill's template, its styles and the
   * balance it reads all remain, and flipping this back to true restores it exactly as it was
   * (subject to the existing CREDIT_EARN check, which still applies on top of this).
   */
  showGathercoCredit = false;

  //Count header credit,quotes,order,sample
  gathercoCreditCount: number = 0;
  public userData: UserModel = new UserModel();
  isTradeUser: boolean = false;
  showSearchBar: boolean = true;
  // quotesCount: any = signal(0);
  // orderCount: any = signal(0);
  sampleOrderCount: any = computed(() => (this.subscriberService.sampleOrders() || []).length);
  destroy$ = new Subject<void>();
  protected readonly CommonConstants = CommonConstants;
  public isCollapsed = true;
  unListen: (() => void) | undefined;
  unListenTouch: (() => void) | undefined;
  isCreditEarnDisabled: any;
  protected readonly isPlatformBrowser = isPlatformBrowser;
  dropDownList = [
    { title: 'Our Story', route: ROUTES.OUR_STORY },
    { title: 'Tiles for Smiles', route: ROUTES.TILES_FOR_SMILES },
    { title: 'Gather Commitment', route: ROUTES.GATHER_COMMITMENT },
    { title: 'Blogs', route: ROUTES.BLOGS.ROOT },
    { title: 'Contact', route: ROUTES.CONTACT },
  ]
  iconList = [
    { title: 'Trade Dashboard', route: `${ROUTES.ACCOUNT.ROOT}/${ROUTES.ACCOUNT.DASHBOARD}`, icon: '/ASSETS/GENERAL/ICONS/dashboard.svg', alt: 'Dashboard' },
    { title: 'Quote', route: `${ROUTES.ACCOUNT.ROOT}/${ROUTES.ACCOUNT.QUOTES}`, icon: '/ASSETS/GENERAL/ICONS/quote.svg', count: 0, width: 20 },
    { title: 'Order', route: `${ROUTES.ACCOUNT.ROOT}/${ROUTES.ACCOUNT.ORDERS}`, icon: '/ASSETS/GENERAL/ICONS/order-icon.svg', count: 0 },
    { title: 'Samples', route: undefined, icon: '/ASSETS/GENERAL/ICONS/sample-icon.svg' }
  ]

  constructor(
    public router: Router,
    private modalService: NgbModal,
    private authService: AuthService,
    private productCollectionService: ProductCollectionService,
    public utilityService: UtilityService,
    public subscriberService: SubscribersService,
    private tokenStorageService: TokenStorageService,
    private quoteOrdersService: QuoteOrdersService,
    private accountServices: AccountService,
    private sampleOrderService: SampleOrderService,
    @Inject(PLATFORM_ID) protected platformId: any,
    private toastService: ToastService,
    private el: ElementRef,
    private renderer: Renderer2
  ) {
    this.isUserAuthenticated = this.authService.isUserAuthenticated();
    this.subscriberService.isUserLoggedIn
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        this.isUserAuthenticated = options;
        if (this.isUserAuthenticated) {
          this.userData = this.tokenStorageService.getUser();
          // this.isCreditEarnData = this.authService.isrCeditEarnDisabled();
          this.isTradeUser = this.userData.role === CommonConstants.USER_TYPE_TRADE;
          if (this.userData.id) {
            this.getCounts();
            this.getQuoteCount();
            this.getOrderCount();
            this.getSampleOrderCount();
          }
        } else {
          this.userData = new UserModel();
        }
      });
    // Listen for route change and scroll to top
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd && typeof window != 'undefined') {
        this.showSearchBar = event.url.split(/[?#]/)[0] != '/' + ROUTES.SEARCH;
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    });
    this.getProductCollectionsByType('flooring');
    this.getProductCollectionsByType('walling');
  }

  ngOnInit(): void {
    if (this.isUserAuthenticated) {
      this.userData = this.tokenStorageService.getUser();
      this.getCounts();
      this.getQuoteCount();
      this.getOrderCount();
      this.getSampleOrderCount();
    }
    this.subscriberService.getCountFormLocalStorage
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.getQuoteCount();
          this.getOrderCount();
        }
      });
    this.subscriberService.headerCreditCountUpdate.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data) this.getCounts();
    });
    this.onSidebarBodyScroll();

    this.subscriberService.getCreditFormLocalStorage.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.isCreditEarnDisabled = data;
    });
  }

  getSampleOrderCount() {
    this.subscriberService.sampleOrders.set(this.tokenStorageService.getSampleOrder());
    this.sampleOrderService.getSampleOrders(this.userData.id, false, true);
  }

  /**
   * Handles window scroll to manage sticky header and navbar visibility.
   */
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const currentScroll =
        window.pageYOffset || document.documentElement.scrollTop;
      this.isHidden.set(
        this.lastScrollTop > 90 && currentScroll > this.lastScrollTop
      );
      this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll; // Prevent negative scroll value
      this.isSticky.set(currentScroll > 0); // Make header sticky on scroll
    }
  }

  // Method to handle mouse hover on the menu items
  onMouseEnterMenu(productType: string, event: Event) {
    this.getProductCollectionsByType(productType);

    // Cast the event target to an HTMLElement to access sibling elements
    const target = event.target as HTMLElement;

    // Find the sibling element with the class 'mega-menu'
    const siblingMegaMenu = target.parentElement?.querySelector('.mega-menu');

    // If the sibling element exists, remove the 'style' attribute
    if (siblingMegaMenu) {
      siblingMegaMenu.removeAttribute('style');
    }
  }

  /**
   * Fetches product collections based on the product type (flooring or walling).
   * Data is cached in local storage to reduce API calls.
   * @param productType The type of product to fetch ('flooring' or 'walling').
   */
  // Method to get collections based on the product type (Flooring or Walling)
  getProductCollectionsByType(productType: string) {
    let collectionKey = '';
    let productTypeId = 0;

    // Determine the collection key and product type ID based on the product type
    if (productType === 'flooring') {
      collectionKey = STORAGE.FLOORING_COLLECTIONS_KEY;
      productTypeId = CommonConstants.FLOORING_TYPE_ID; // Flooring type ID
    } else if (productType === 'walling') {
      collectionKey = STORAGE.WALLING_COLLECTIONS_KEY;
      productTypeId = CommonConstants.WALLING_TYPE_ID; // Walling type ID
    }

    // Check if collections data is available in local storage
    const collectionDataString = this.tokenStorageService.getItemStorage(collectionKey);
    if (collectionDataString) {
      const collectionData = JSON.parse(collectionDataString);
      if (collectionData) {
        if (productType === 'flooring') {
          this.flooringCollectionList = collectionData; // Load flooring data
        } else if (productType === 'walling') {
          this.wallingCollectionList = collectionData; // Load walling data
        }
      }
    }

    // If no data in local storage, fetch from API
    const requestParamModel = this.createRequestParamModel(productTypeId);
    this.productCollectionService
      .getCollections(requestParamModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (dataList: PaginationModel<any>) => {
          requestParamModel.update<any>(dataList); // Update pagination
          // Save data to local storage based on the product type
          if (productType === 'flooring') {
            this.flooringCollectionList = dataList.data;
            this.tokenStorageService.setItemStorage(
              STORAGE.FLOORING_COLLECTIONS_KEY,
              JSON.stringify(dataList.data)
            );
          } else if (productType === 'walling') {
            this.wallingCollectionList = dataList.data;
            this.tokenStorageService.setItemStorage(
              STORAGE.WALLING_COLLECTIONS_KEY,
              JSON.stringify(dataList.data)
            );
          }
        },
        (error: HttpErrorResponse) => {
          this.toastService.showError(error);
        }
      );
  }

  /**
   * Creates a request parameter model based on the provided product type ID.
   * @param productTypeId The ID of the product type (flooring or walling).
   * @returns A populated RequestParamModel object.
   */
  private createRequestParamModel(productTypeId: number): RequestParamModel {
    const requestParamModel = new RequestParamModel();
    requestParamModel.pageSize = CommonConstants.PAGE_SIZE_ALL; // Set page size to fetch all
    requestParamModel.fields = ['id', 'name']; // Specify fields to retrieve
    requestParamModel.addDefaultFilter(
      'product_type_id',
      productTypeId,
      'number'
    ); // Filter by product type ID
    requestParamModel.sortChange('name', 'asc'); // Sort by name in ascending order
    return requestParamModel;
  }

  /**
   * Handles the search event and redirects to the search page with the query.
   * @param event The event object.
   * @param value The search query entered by the user.
   */
  onSearch(event: any, value: any) {
    event.preventDefault();
    this.tokenStorageService.setSearchFields({ search_keyword: value.trim() });
    this.router.navigate([ROUTES.SEARCH]);
  }

  getCounts() {
    if (this.userData.company_id) {
      const requestParamModel = new RequestParamModel();
      requestParamModel.expand = ['gathercoCredit'];
      requestParamModel.fields = ['gathercoCredit.available_credit', 'is_credit_earn_disabled'];
      delete requestParamModel.pageSize;

      this.accountServices.getAccountDetails(this.userData.company_id, requestParamModel).pipe(takeUntil(this.destroy$)).subscribe(
        (data: any) => {
          if (data) {
            this.tokenStorageService.setCreditEarn(data.is_credit_earn_disabled);
            this.subscriberService.getCreditEarnFormLocalStorage(data.is_credit_earn_disabled);
            this.gathercoCreditCount = data.gathercoCredit?.available_credit > 0 ? data.gathercoCredit.available_credit : 0;
          } else this.gathercoCreditCount = 0;
        }, () => {
          this.gathercoCreditCount = 0;
        });
    }
  }

  /** Profile menu → Logout: ask first, and sign out only on Logout. */
  async confirmLogout(): Promise<void> {
    if (await askLogoutConfirmation(this.modalService)) {
      this.logout();
    }
  }

  /**
   * Logs out the user by clearing authentication tokens.
   */
  logout(): void {
    this.authService.logout().subscribe(() => {
      this.tokenStorageService.clearStorage();
      this.isUserAuthenticated = false; // Update authentication state
      this.subscriberService.isUserLoggedIn.next(false);
      this.router.navigate(['/']);
    });
  }

  handlePriceListClick() {
    this.toggleMenu();
    if (!this.isUserAuthenticated) {
      this.toastService.showError(CommonConstants.PREVENT_ACCESS_PRICE_LIST_ERROR_MESSAGE);
    }
  }

  toggleMenu(menu?: any) {
    this.isCollapsed = !this.isCollapsed;
    if (window.innerWidth < 1240) {
      this.menuIcon.nativeElement.click();
    }

    if (menu == 'pricelist' && !this.isUserAuthenticated) {
      this.toastService.showError(CommonConstants.PREVENT_ACCESS_PRICE_LIST_ERROR_MESSAGE);
    }
  }

  toggleMegaMenu(event: Event) {
    const target = event.target as HTMLElement;
    const megaMenu = target.closest('.mega-menu');
    if (megaMenu) {
      (megaMenu as HTMLElement).style.visibility = 'hidden';
    }
  }

  getQuoteCount() {
    const requestParamModel = new RequestParamModel();
    requestParamModel.addDefaultFilters('frontend_user_id', this.userData.id, 'number');
    requestParamModel.addDefaultFilters('type', CommonConstants.QUOTE_TYPE_QUOTE, 'number');
    requestParamModel.addDefaultFilters('is_expired', CommonConstants.QUOTE_IS_EXPIRED_NO, 'number');
    requestParamModel.addDefaultFilters('is_draft', CommonConstants.QUOTE_IS_NOT_DRAFT, 'number');
    requestParamModel.addDefaultFilters('is_deleted', CommonConstants.QUOTE_IS_DELETE_NO, 'number');
    requestParamModel.addDefaultFilters('is_archive', CommonConstants.QUOTE_IS_ARCHIVED_FALSE, 'number');
    requestParamModel.fields = ['id'];
    this.quoteOrdersService
      .getQuoteOrders(requestParamModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any) => {
          this.iconList[1].count = data.totalCount;
          // this.quotesCount.set(data.totalCount);
        },
        (error: HttpErrorResponse) => {
          this.toastService.showError(error);
        }
      );
  }

  getOrderCount() {
    const requestParamModel = new RequestParamModel();
    requestParamModel.addDefaultFilters('frontend_user_id', this.userData.id, 'number');
    requestParamModel.addDefaultFilters('order_type', CommonConstants.ORDER_TAB_ALL_SHORT, 'number');
    // requestParamModel.addDefaultFilters('is_accepted', CommonConstants.QUOTE_IS_ACCEPTED_YES, 'number');
    requestParamModel.addDefaultFilters('is_expired', CommonConstants.QUOTE_IS_EXPIRED_NO, 'number');
    requestParamModel.addDefaultFilters('is_deleted', CommonConstants.QUOTE_IS_DELETE_NO, 'number');
    requestParamModel.fields = ['id'];
    this.quoteOrdersService
      .getQuoteOrders(requestParamModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any) => {
          // this.orderCount.set(data.totalCount);
          this.iconList[2].count = data.totalCount;
        },
        (error: HttpErrorResponse) => {
          this.toastService.showError(error);
        }
      );
  }

  async openSampleOrderModal() {
    let sample: any = await this.sampleOrderService.getSampleOrders(this.userData.id, true);
    if (!sample?.error && !this.subscriberService.sampleOrderError()) {
      const { ModalSampleOrderComponent } = await import('src/app/features/price-list/modal-sample-order/modal-sample-order.component');
      this.modalService.open(ModalSampleOrderComponent, { windowClass: 'product-details-modal' });
    }
  }

  onSidebarBodyScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const scrollableDiv = this.el.nativeElement.querySelector('.navbar-list');
      this.unListen = this.renderer?.listen(
        scrollableDiv,
        'wheel',
        (event: WheelEvent) => {
          this.preventScroll(event, scrollableDiv);
        }
      );

      this.unListenTouch = this.renderer?.listen(
        scrollableDiv,
        'touchmove',
        (event: TouchEvent) => {
          this.preventScroll(event, scrollableDiv);
        }
      );
    }
  }

  preventScroll(event: Event, scrollableDiv: HTMLElement) {
    const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;
    const atTop =
      scrollTop === 0 &&
      (event instanceof WheelEvent
        ? event.deltaY < 0
        : event instanceof TouchEvent
          ? event.touches[0].clientY > scrollTop
          : false);
    const atBottom =
      scrollTop + clientHeight === scrollHeight &&
      (event instanceof WheelEvent
        ? event.deltaY > 0
        : event instanceof TouchEvent
          ? event.touches[0].clientY < scrollTop + clientHeight
          : false);

    if (atTop || atBottom) {
      event.preventDefault();
    }
  }

  async openModal(model?: any) {
    const { AuthComponent } = await import('../../../../features/auth/auth.component');
    const modelRef = this.modalService.open(AuthComponent, {
      scrollable: true,
      modalDialogClass: 'auth-modal',
    });
    if(model) modelRef.componentInstance.authModel = model;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.unListen) {
      this.unListen();
    }
    if (this.unListenTouch) {
      this.unListenTouch();
    }
  }
}
